<script setup>
import ConnectorItem from '@/components/front/combination/ConnectorItem.vue';
import ModalBodyButtons from '@/components/front/combination/ModalBodyButtons.vue';
import ModalBodyCategoryList from '@/components/front/combination/ModalBodyCategoryList.vue';
import ModalBodyProductList from '@/components/front/combination/ModalBodyProductList.vue';
import ModalBodyProductItem from '@/components/front/combination/ModalBodyProductItem.vue';
import BsModal from '@/components/bootstrap/BsModal.vue';
import CombinationService from '@/services/combination-service';
import combinationStorage from '@/services/combination-storage';
import { computed, defineOptions, onMounted, reactive, ref, shallowRef } from 'vue';

defineOptions({
  name: 'CombinationProductCables'
})

const modalTitle = ref('線材');
const modalBodyComponents = shallowRef({
  'buttons': ModalBodyButtons,
  'categoryList': ModalBodyCategoryList,
  'productList': ModalBodyProductList,
  'productItem': ModalBodyProductItem
});

combinationStorage.categoryType = 'cable';

const initialItem = combinationStorage.initialItem;
const terminalItem = combinationStorage.terminalItem;
const state = reactive({
  cables: {},
  options: {}
})

onMounted(() => {
  prepareView();
})

async function prepareView() {
  let pins = initialItem?.pin || 1;

  combinationStorage.mappingIds = null;

  for (let i = 1; i<= pins; i++) {
    state.cables[i] = combinationStorage.cables[i] ?? {
      variant: null,
      initial: {
        pin: combinationStorage.onlyCables ? null : i,
        category: null
      },
      terminal: {
        pin: null,
        category: null
      },
      allowNull: false,
      isValid: true,
    }
  }

  pins = terminalItem?.pin || 0;

  for (let i = 1; i<= pins; i++) {
    if (state.cables[i]) {
      state.cables[i].terminal.pin = combinationStorage.onlyCables ? null : i;
    }

    state.options[i] = {
      value: i,
      selected: false,
    }
  }

  combinationStorage.cables = state.cables;

  Object.values(state.cables).map((v) => {
    if (v.terminal.pin) {
      state.options[v.terminal.pin].selected = true;
    }
  });
}

function setTerminalValue(cableKey, optionKey, value) {
  const oldPin = state.cables[cableKey].terminal.pin;

  Object.values(state.options).map((v) => {
    if (v.value === oldPin) {
      v.selected = false;
    }
  });

  state.cables[cableKey].terminal.pin = value;

  if(value) {
    state.options[optionKey].selected = !!state.cables[cableKey].terminal.pin;
  }
}

function handleModalState(key) {
  combinationStorage.currentEditCable = key;
  combinationStorage.modalType = 'cable';
  combinationStorage.modalCurrentComponent = 'buttons';
  combinationStorage.modalState = true;
}

function handleEdit(key) {
  combinationStorage.currentEditCable = key;
  combinationStorage.modalType = 'cable';
  combinationStorage.productId = state.cables[key].variant.productId;
  combinationStorage.modalCurrentComponent = 'productItem';

  combinationStorage.modalState = true;
}

function handleAllowNull(index) {
  if (state.cables[index].allowNull) {
    Object.values(state.options).map((v) => {
      if (v.value === state.cables[index].terminal.pin) {
        v.selected = false;
      }
    });

    state.cables[index].terminal.pin = null;
  }
}

const removeModalState = ref(false);
const removeTarget = ref(null);

function handleRemove(key) {
  removeTarget.value = key;
  removeModalState.value = true;
}

function remove() {
  state.cables[removeTarget.value].variant = null;
  state.cables[removeTarget.value].initial.category = null;
  state.cables[removeTarget.value].terminal.category = null;

  removeModalState.value = false;
  removeTarget.value = null;
}

function cancelRemove() {
  removeModalState.value = false;
  removeTarget.value = null;
}

const syncCable = ref('');

function syncEmptyCables() {
  if (!syncCable.value) {
    return
  }

  for (let i = 1; i<= Object.values(state.cables).length; i++) {
    if (!state.cables[i].variant) {
      state.cables[i].variant = state.cables[syncCable.value].variant;
    }
  }
}

const imgSample2Path = u.$uri.asset.path('images/front/order/combination-step-2_16x9.png');
</script>

<template>
  <div class="l-product-cable">
    <section class="l-product-cable__order">
      <div class="container-fluid">
        <div class="row" v-if="initialItem">
          <div class="col d-flex flex-column align-items-center">
            <div class="l-product-cable__img d-flex justify-content-center ratio ratio-16x9">
              <img :src="imgSample2Path" class="object-fit-cover" alt="imgSample2">
            </div>

            <p class="l-product-port__tip text-primary text-center mx-auto mb-5">
              <i class="fa-regular fa-circle-info me-1"></i>有選擇終端孔位的所有線材，需要設定相同長度。若長度不同則會依照最短長度進行製作。
            </p>
          </div>
        </div>

        <div class="row justify-content-start justify-content-lg-center mb-4" v-if="initialItem">
          <div class="col-auto d-flex align-items-center gap-3">
            <span>將</span>
            <span>第</span>
            <select v-model="syncCable" class="l-product-cable__sync form-select fs-6">
              <option value="">-</option>
              <option v-for="pin in initialItem.pin">{{ pin }}</option>
            </select>
            <span>孔</span>
          </div>

          <div class="col-auto d-flex align-items-center gap-3">
            <span>的線材規格套用至全部未選取的線材上</span>
            <button type="button" class="btn btn-dark"
              @click="syncEmptyCables"
            >套用
            </button>
          </div>
        </div>

        <div class="row mb-5"
          :class="!initialItem && !terminalItem ? 'justify-content-center' : ''"
        >
          <div class="col-6 col-xl-3 mb-4 mb-xl-0" v-if="initialItem">
            <p class="l-product-cable__thead mb-0 py-3 px-4 text-center">起始端</p>
            <ConnectorItem :item="initialItem"></ConnectorItem>
          </div>
          <div class="col-6 col-xl-3 mb-4 mb-xl-0 d-xl-none"
            v-if="terminalItem">
            <p class="l-product-cable__thead mb-0 py-3 px-4 text-center">終端</p>
            <ConnectorItem :item="terminalItem"></ConnectorItem>
          </div>

          <div class="col-12 col-xl-6">
            <p class="l-product-cable__thead mb-0 py-3 d-none d-md-flex">
              <span class="l-product-cable__thead--input px-4">起始端孔位</span>
              <span class="l-product-cable__thead--cable px-4">線材型號及長度</span>
              <span class="l-product-cable__thead--output px-4">終端孔位</span>
            </p>

            <ol class="l-product-cable__list mb-0 ps-0">
              <li class="l-product-cable__item pt-md-4 pb-md-5 d-flex align-items-md-center mb-4 mb-md-0"
                v-for="(cable, i) in state.cables"
                :key="`cable-` + i">
                <p class="l-product-cable__item-thead mb-0 d-flex flex-column justify-content-between d-md-none">
                  <span class="l-product-cable__item-thead--input px-3 d-flex align-items-center">起始端孔位</span>
                  <span class="l-product-cable__item-thead--cable px-3">線材型號及長度</span>
                  <span class="l-product-cable__item-thead--output px-3 d-flex align-items-center">終端孔位</span>
                </p>

                <div
                  class="l-product-cable__item-wrapper d-flex justify-content-between align-items-md-center flex-column flex-md-row w-100">
                  <div class="l-product-cable__item--input d-flex align-items-center gap-2 px-3 px-md-4"
                    v-if="initialItem">
                    <span>第</span>
                    <span class="text-dark">{{ cable.initial.pin }}</span>
                    <span>孔</span>
                  </div>
                  <div
                    class="l-product-cable__item--input fw-bold px-3 d-flex justify-content-center align-items-center"
                    v-else>
                    -
                  </div>

                  <div class="l-product-cable__item--cable px-3 px-md-4">
                    <template v-if="!cable.variant">
                      <button type="button" class="c-choose-btn btn text-dark w-100 px-1 mb-1 mb-md-0"
                        :disabled="cable.allowNull"
                        @click="handleModalState(i)" >
                        <i class="fa-solid fa-circle-plus me-2"></i>
                        選擇線材規格
                      </button>

                      <div v-if="!cable.variant && !combinationStorage.onlyCables" class="form-check l-product-cable__item--no-cable">
                        <input v-model="cable.allowNull" type="checkbox" :id="`non-cables-` + i" class="form-check-input"
                          @change="handleAllowNull(i)"
                        />
                        <label :for="`non-cables-` + i" class="form-check-label">此孔位不連接線材</label>
                      </div>
                    </template>

                    <div class="l-product-cable__info d-flex justify-content-between align-items-center gap-2"
                      v-else>
                      <div class="l-product-cable__info-wrapper">
                        <h2 class="h5 mb-0 fw-medium">
                          {{ cable.variant.product.title }}
                        </h2>
                        <div class="d-md-flex align-items-center gap-2 mb-1">
                          <p class="mb-0">
                            {{ cable.variant.product.model }}
                          </p>

                          <template v-if="cable.variant.sku">
                            <p class="mb-0">-</p>
                            <p class="mb-0">{{ cable.variant.sku }}</p>
                          </template>
                        </div>

                        <div v-if="!cable.variant.primary" class="c-port-card__specifications text-dark mb-0">
                          {{ CombinationService.getVariantTitleText(cable.variant.title) }}
                        </div>

                        <p>長度 <span class="text-dark">{{ cable.variant.length }}</span> 公分</p>
                      </div>
                      <ul class="l-product-cable__edit-btns d-flex flex-column flex-md-row gap-3 mb-0 ps-0">
                        <li>
                          <button type="button" class="btn p-0 fs-5" @click="handleEdit(i)">
                            <i class="fa-solid fa-pen"></i>
                          </button>
                        </li>
                        <li>
                          <button type="button" class="btn p-0 fs-5" @click="handleRemove(i)">
                            <i class="fa-solid fa-trash"></i>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div class="l-product-cable__item--output px-3 d-flex align-items-center"
                    v-if="terminalItem">
                    <div class="dropdown l-product-cable__dropdown w-100">
                      <button class="btn c-choose-btn text-dark dropdown-toggle d-flex justify-content-between align-items-center w-100"
                        :disabled="cable.allowNull"
                        type="button" :id="`dropdown-` + i" data-bs-toggle="dropdown" aria-expanded="false">
                        {{ cable.terminal.pin ? cable.terminal.pin : '無需插膠殼孔位' }}
                      </button>
                      <ul class="dropdown-menu" :aria-labelledby="`dropdown-` + i">
                        <li>
                          <a class="dropdown-item" href="javascript://"
                            @click="setTerminalValue(i, 0, null)">
                            無需插膠殼孔位
                          </a>
                        </li>

                        <li v-for="(option, k) in state.options">
                          <a class="dropdown-item" href="javascript://"
                            @click="setTerminalValue(i, k, option.value)">
                            {{ option.value }}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div
                    class="l-product-cable__item--output fw-bold px-3 px-md-4 d-flex justify-content-center align-items-center"
                    v-else>
                    -
                  </div>
                </div>
              </li>
            </ol>
          </div>

          <div class="col-3 d-none d-xl-block" v-if="initialItem && terminalItem">
            <p class="l-product-cable__thead mb-0 py-3 px-4 text-center">終端</p>
            <ConnectorItem :item="terminalItem"></ConnectorItem>
          </div>
        </div>
      </div>
    </section>

    <div class="c-accordion accordion d-xl-none fixed-bottom bg-white" id="accordionSync"
      v-if="initialItem && terminalItem">
      <div class="accordion-item border-0">
        <h3 class="accordion-header">
          <button class="accordion-button collapsed h5 bg-white text-dark mb-0" type="button" data-bs-toggle="collapse"
            data-bs-target="#collapseOne"
            aria-expanded="true" aria-controls="collapseOne">
            套用線材規格
          </button>
        </h3>
        <div id="collapseOne" class="accordion-collapse collapse" data-bs-parent="#accordionSync">
          <div class="accordion-body">
            <div class="d-flex flex-wrap justify-content-center align-items-center gap-3">
              <span>將</span>
              <span>第</span>
              <select v-model="syncCable" class="l-product-cable__sync form-select fs-6">
                <option value="">-</option>
                <option v-for="pin in initialItem.pin">{{ pin }}</option>
              </select>
              <span>孔</span>
              <span>的線材規格套用至全部未選取的線材上</span>
              <button type="button" class="btn btn-dark"
                @click="syncEmptyCables"
              >套用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <BsModal
      :open="combinationStorage.modalState"
      class="l-product-port__modal"
      :scrollable="true"
      @hidden="combinationStorage.modalState = false"
    >
      <template #header-element>
        <div class="modal-header">
          <h2 class="modal-title h5" id="productModalLabel">選擇{{ modalTitle }}</h2>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <component :is="modalBodyComponents[combinationStorage.modalCurrentComponent]"></component>
      </template>
    </BsModal>

    <BsModal
      :open="removeModalState"
      class="l-product-port__delete-modal"
      :centered="true"
      @hidden="removeModalState = false">
      <h3 class="h5 text-center mb-4">確認要刪除嗎？</h3>
      <div class="d-flex gap-2 justify-content-center">
        <button class="btn btn-primary" @click="remove">確認</button>
        <button class="btn btn-dark" @click="cancelRemove">取消</button>
      </div>
    </BsModal>
  </div>
</template>
